'use client'

import { useCallback, useRef } from 'react'

import { hllbnt } from './config'
import { HllbntMark, type HllbntMarkHandle } from './hllbnt-mark'

import './footer-link.css'

interface HellbentFooterLinkProps {
  clientSlug: string
}

/**
 * "Built by HLLBNT" link rendered in the public-site footer. The animated
 * brand mark is the shared `HllbntMark` component so the footer and the
 * admin chrome share one visual identity.
 *
 * Hover/focus/click are owned by the anchor (so the entire link area
 * triggers the scramble, including the "Built by" label) and forwarded to
 * the mark via an imperative ref.
 */
export function HellbentFooterLink({ clientSlug }: HellbentFooterLinkProps) {
  const markRef = useRef<HllbntMarkHandle>(null)

  const onEnter = useCallback(() => markRef.current?.startScramble(), [])
  const onLeave = useCallback(() => markRef.current?.startIdle(), [])
  const onClick = useCallback(() => markRef.current?.fireGlitch(), [])

  return (
    <a
      href={hllbnt.footerUrl(clientSlug)}
      target="_blank"
      rel="noopener noreferrer"
      className="hllbnt-link group inline-flex items-center gap-1.5"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      onClick={onClick}
    >
      <span className="hllbnt-link-label text-xs tracking-wide text-white/20 transition-all duration-300 group-hover:text-white/40">
        Built by
      </span>
      <HllbntMark ref={markRef} text="HLLBNT" interactive={false} ariaLabel="Hellbent" />
    </a>
  )
}
